'use client';

import { useBreakpoint } from '@repo/react-hooks';
import { Button } from '@repo/ui/button';
import CarouselBanners, { type BannerType } from '@repo/ui/carousel-banners';
import { cn } from '@repo/utils/cn';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { UnStyledLink } from '@/components/common/link';
import BannerSkeleton from '@/components/common/skeletons/banner-skeleton';
import { APP_REVALIDATE_QUERY_TIMES } from '@/constants';
import { type BannersRequestType } from '@/lib/fetch';
import { useTenant } from '@/providers/TenantProvider';
import { getBanners } from '@/server-actions/banners';
import { getBannersFallback } from './configs';
import { type BannerCategoryType } from './configs/types';

interface AppBannersProps {
  type: BannersRequestType['pageType'];
}

// Map API page types to fallback banner categories
const mapPageTypeToFallbackCategory = (pageType: BannersRequestType['pageType']): keyof BannerCategoryType => {
  switch (pageType) {
    case 'LANDING':
    case 'HOMEPAGE':
      return 'LANDING';
    case 'CASINO':
      return 'CASINO';
    case 'SPORTS':
      return 'SPORTSBOOK';
    default:
      return 'LANDING';
  }
};

const AppBanners = ({ type }: AppBannersProps) => {
  const t = useTranslations('carousel-banners');
  const { matches: isMobile, isLoading: isBreakpointLoading } = useBreakpoint('sm', 'max');

  const { tenantMachineName } = useTenant();

  const { data: apiBanners, isLoading: isApiLoading } = useQuery({
    queryKey: ['banners', tenantMachineName, isMobile, type],
    queryFn: () => getBanners({ pageType: type, deviceType: isMobile ? 'MOBILE' : 'DESKTOP' }),
    select: (data) => data?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    staleTime: APP_REVALIDATE_QUERY_TIMES.BANNERS,
  });

  // Temporarily fallback to the default banners for the page type
  const { data: fallbackBanners, isLoading: isFallbackLoading } = useQuery({
    queryKey: ['fallback-banners', tenantMachineName, isMobile, type],
    queryFn: () => getBannersFallback(t, tenantMachineName, isMobile),
    select: (data) => data[mapPageTypeToFallbackCategory(type)] || [],
    staleTime: APP_REVALIDATE_QUERY_TIMES.BANNERS,
    enabled: !!tenantMachineName,
  });

  const finalBanners = useMemo(() => {
    if (!apiBanners || !fallbackBanners) return apiBanners || [];

    const TARGET_BANNER_COUNT = 3;

    if (apiBanners.length >= TARGET_BANNER_COUNT) {
      return apiBanners;
    }

    const remainingCount = TARGET_BANNER_COUNT - apiBanners.length;
    const additionalBanners = fallbackBanners.slice(0, remainingCount);

    return [...apiBanners, ...additionalBanners];
  }, [apiBanners, fallbackBanners]);

  if (isApiLoading || isFallbackLoading || isBreakpointLoading) {
    return <BannerSkeleton className="mb-3" />;
  }

  if (!finalBanners?.length) {
    return null;
  }

  const bannerButtonRenderer = (banner: BannerType) => {
    return (
      <Button
        className="rounded-3xl px-4 transition-all duration-300 vt-text-size-small sm:px-6 sm:vt-text-size-medium-sm"
        size="sm"
        asChild
      >
        <UnStyledLink
          href={banner.redirectUrl || '/'}
          target={banner.target || '_self'}
        >
          <span className="uppercase">{banner.buttonLabel || t(`${type.toLowerCase()}-button-title`)}</span>
        </UnStyledLink>
      </Button>
    );
  };

  return (
    <CarouselBanners
      banners={finalBanners}
      autoplay={false}
      bannerButtonRenderer={bannerButtonRenderer}
      bannerContentClassName="gap-y-2"
      carouselItemClassName={cn('md:basis-1/2 lg:basis-1/3 xl:basis-1/3 rounded-lg', {
        'flex-[0_0_90%] mr-1 pr-2': finalBanners?.length > 1,
        'flex-[0_0_100%]': finalBanners?.length === 1,
      })}
      carouseIndicatorClassName="hidden sm:flex"
      carouselLeftIndicatorClassName={cn('-left-[20px]', {
        hidden: type === 'LANDING',
      })}
      carouselRightIndicatorClassName={cn('-right-[20px]', {
        hidden: type === 'LANDING',
      })}
      align="center"
    />
  );
};

export default AppBanners;
